import {
  AsyncThunk,
  createEntityAdapter,
  createSlice,
  EntityId,
  PayloadAction,
} from '@reduxjs/toolkit';

import { Patient } from '@/shared/api/protocol-ts/model/dto_patient_pb';
import {
  DefaultInitialState,
  LoadingStateType,
  SliceName,
} from '@/shared/config';

import { LastReportAssetIDs } from '..';

import {
  createPatient,
  deletePatient,
  setPatientClinicalCaseDescription,
} from './patientSlice.thunks';

export const patientAdapter = createEntityAdapter<Patient>({
  selectId: (patient) => patient.ID,
  // The system should display patients sorted by Last modified date in descending order
  // sortComparer: (a, b) => b.Revision.UpdatedAt - a.Revision.UpdatedAt,
});

type PatientSliceInitialState = DefaultInitialState & {
  currentPatient: Patient | undefined;
  flags: {
    isCreatePatientPending: boolean;
    isDeletePatientPending: boolean;
  };
  sharedWithMeCount: number;
  sharedByMeCount: number;
  totalOrganizationPatientCount: number;
  lastReportAssetIDs: LastReportAssetIDs;
};

const initialStateFlags: PatientSliceInitialState = {
  currentPatient: undefined,
  flags: {
    isCreatePatientPending: false,
    isDeletePatientPending: false,
  },
  sharedWithMeCount: 0,
  sharedByMeCount: 0,
  totalOrganizationPatientCount: 0,
  lastReportAssetIDs: {},
  loading: 'idle',
};

type StateFlags = keyof typeof initialStateFlags.flags;

const patientState = patientAdapter.getInitialState(initialStateFlags);

type PatientState = typeof patientState;

const setNewestOne = (state: PatientState, action: PayloadAction<Patient>) => {
  const currentRevisionNumber =
    state.entities[action.payload.ID]?.Revision?.Number ?? 0;
  const payloadRevisionNumber = action.payload.Revision?.Number ?? 0;

  if (action.payload.Deleted?.Deleted) {
    patientAdapter.removeOne(state as PatientState, action.payload.ID);
  } else if (payloadRevisionNumber > currentRevisionNumber) {
    patientAdapter.setOne(state as PatientState, action.payload);
  }
};

const patientSlice = createSlice({
  name: SliceName.patient,
  initialState: patientAdapter.getInitialState(initialStateFlags),
  reducers: {
    setCurrentPatient: (state, action: PayloadAction<Patient>) => {
      state.currentPatient = action.payload;
    },
    updateCurrentPatient: (state, action: PayloadAction<Patient>) => {
      const currentRevisionNumber = state.currentPatient?.Revision?.Number ?? 0;
      const payloadRevisionNumber = action.payload.Revision?.Number ?? 0;

      if (payloadRevisionNumber > currentRevisionNumber) {
        state.currentPatient = action.payload;
      }
    },
    removeCurrentPatient: (state) => {
      state.currentPatient = undefined;
    },
    addOne: (state, action: PayloadAction<Patient>) => {
      patientAdapter.addOne(state as PatientState, action.payload);
    },
    addMany: (state, action: PayloadAction<Patient[]>) => {
      patientAdapter.addMany(state as PatientState, action.payload);
    },
    removeAll: (state) => {
      patientAdapter.removeAll(state as PatientState);
    },
    setOne: (state, action: PayloadAction<Patient>) => {
      patientAdapter.setOne(state as PatientState, action.payload);
    },
    removeOne: (state, action: PayloadAction<EntityId>) => {
      patientAdapter.removeOne(state as PatientState, action.payload);
    },
    setNewestOne: (state, action: PayloadAction<Patient>) => {
      setNewestOne(state as PatientState, action);
    },
    setMany: (state, action) => {
      patientAdapter.removeAll(state as PatientState);
      patientAdapter.addMany(state as PatientState, action.payload);
    },
    setSharedWithMeCount: (state, action: PayloadAction<number>) => {
      state.sharedWithMeCount = action.payload;
    },
    setSharedByMeCount: (state, action: PayloadAction<number>) => {
      state.sharedByMeCount = action.payload;
    },
    setTotalOrganizationPatientCount: (
      state,
      action: PayloadAction<number>,
    ) => {
      state.totalOrganizationPatientCount = action.payload;
    },
    setLoading: (state, action: PayloadAction<LoadingStateType>) => {
      state.loading = action.payload;
    },
    patientListLoadingPending: (state) => {
      state.loading = 'pending';
    },
    patientListLoadingSucceeded: (state) => {
      state.loading = 'succeeded';
    },
    patientListLoadingFailed: (state) => {
      state.loading = 'failed';
    },
  },
  extraReducers: (builder) => {
    const asyncRequestCreator = <A, B, C>(
      // @ts-expect-error WARN: C doesn't satisfy AsyncThunkConfig, but this is not public type. Just ignore this or remove this function and use builder itself.
      thunkAction: AsyncThunk<A, B, C>,
      pendingFlagKey: StateFlags,
      fulfilledCallback?: (payload: unknown) => void,
    ) => {
      builder.addCase(thunkAction.pending, (state) => {
        state.flags[pendingFlagKey] = true;
      });
      builder.addCase(thunkAction.fulfilled, (state, { payload }) => {
        state.flags[pendingFlagKey] = false;
        if (typeof fulfilledCallback === 'function') {
          fulfilledCallback(payload);
        }
      });
      builder.addCase(thunkAction.rejected, (state) => {
        state.flags[pendingFlagKey] = false;
      });
    };

    builder.addCase(
      setPatientClinicalCaseDescription.fulfilled,
      (state, action) => {
        const { Patient } = action.payload;

        if (Patient) {
          setNewestOne(state as PatientState, {
            type: `${SliceName.patient}/setNewestOne`,
            payload: Patient,
          });
        }
      },
    );

    asyncRequestCreator(createPatient, 'isCreatePatientPending');
    asyncRequestCreator(deletePatient, 'isDeletePatientPending');
  },
});

export const { actions } = patientSlice;

export default patientSlice.reducer;
